import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, resolveMediaUrl } from '../utils/api';
import { useCart } from '../contexts/CartContext';
import { useNotification } from '../contexts/NotificationContext';
import { useSavedProducts } from '../contexts/SavedProductsContext';
import { useAuth } from '../contexts/AuthContext';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { showAlert } = useNotification();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useSavedProducts();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainImageIndex, setMainImageIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    api
      .getProduct(id)
      .then((data) => {
        if (!cancelled) setProduct(data);
      })
      .catch(() => {
        if (!cancelled) setProduct(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    api
      .getRecommendedProducts({
        context: 'product_detail',
        productId: product.id,
        limit: 4,
        excludeIds: [product.id],
      })
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          const list = data.filter((p) => p.id !== product.id).slice(0, 4);
          setRelated(list);
        }
      })
      .catch(() =>
        api
          .getProducts(null, product.category || null, 0, 20)
          .then((fallbackData) => {
            if (!cancelled && Array.isArray(fallbackData)) {
              setRelated(fallbackData.filter((p) => p.id !== product.id).slice(0, 4));
            }
          })
          .catch(() => {
            if (!cancelled) setRelated([]);
          })
      );
    return () => { cancelled = true; };
  }, [product]);

  useEffect(() => {
    if (!product?.id) return;
    api.trackProductEvent({ productId: product.id, eventType: 'view', context: 'product_detail' }).catch(() => {});
  }, [product?.id]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg-base)',
          color: 'var(--text-primary)',
          padding: '7rem 1.5rem 5rem',
          fontFamily: 'var(--font-body)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ color: 'var(--text-muted)' }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (!product) {
    navigate('/products', { replace: true });
    return null;
  }

  const images = product.photo_url ? [product.photo_url] : [];

  const pageStyle = {
    minHeight: '100vh',
    background: 'var(--bg-base)',
    color: 'var(--text-primary)',
    padding: '7rem 1.5rem 5rem',
    fontFamily: 'var(--font-body)',
  };

  const breadcrumbStyle = {
    marginBottom: '1.5rem',
    fontSize: '0.875rem',
    color: 'var(--text-muted)',
  };

  const linkStyle = { color: 'var(--orange)', textDecoration: 'none' };

  const cardStyle = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-card)',
    padding: '1rem',
    textAlign: 'center',
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <nav style={breadcrumbStyle} aria-label="Breadcrumb">
          <Link to="/" style={linkStyle}>
            {t('nav.home')}
          </Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          <Link to="/products" style={linkStyle}>
            {t('products.title')}
          </Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          <span>{product.name}</span>
        </nav>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2.5rem',
            alignItems: 'start',
          }}
          className="product-detail-layout"
        >
          <div>
            <div
              style={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                background: 'var(--bg-dark)',
                border: '1px solid var(--border)',
                marginBottom: '1rem',
              }}
            >
              {images[mainImageIndex] ? (
                <img
                  src={resolveMediaUrl(images[mainImageIndex])}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ fontSize: '4rem', opacity: 0.5 }}>🧴</span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setMainImageIndex(idx)}
                    style={{
                      width: '64px',
                      height: '64px',
                      padding: 0,
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      border: `2px solid ${mainImageIndex === idx ? 'var(--orange)' : 'var(--border)'}`,
                      background: 'var(--bg-dark)',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={resolveMediaUrl(img)}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <h1
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.75rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                marginBottom: '0.75rem',
                lineHeight: 1.2,
              }}
            >
              {product.name}
            </h1>
            <p
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--orange)',
                marginBottom: '1rem',
              }}
            >
              {product.price} EGP
            </p>
            <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              ★ {t('products.noRatingsYet')}
            </div>
            <p
              style={{
                fontSize: '0.95rem',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
                marginBottom: '1.5rem',
              }}
            >
              {product.description || t('products.premiumProductPlaceholder')}
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  color: 'var(--text-muted)',
                  marginRight: '0.5rem',
                }}
              >
                {t('products.color')}:
              </span>
              <span style={{ fontSize: '0.9rem' }}>{t('products.defaultOption')}</span>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  color: 'var(--text-muted)',
                  marginRight: '0.5rem',
                }}
              >
                {t('products.size')}:
              </span>
              <span style={{ fontSize: '0.9rem' }}>{t('products.oneSize')}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  addToCart(product);
                  api.trackProductEvent({ productId: product.id, eventType: 'add_to_cart', context: 'product_detail' }).catch(() => {});
                  showAlert(t('cart.addedToCart'));
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.25rem',
                  background: 'var(--orange)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  cursor: 'pointer',
                }}
              >
                {t('products.addToCart')}
              </button>
              <button
                type="button"
                aria-label="Wishlist"
                onClick={async () => {
                  if (!user) {
                    showAlert('Please login to use wishlist');
                    return;
                  }
                  try {
                    const added = await toggleWishlist(product.id);
                    showAlert(added ? 'Added to wishlist' : 'Removed from wishlist');
                  } catch (err) {
                    showAlert(err.message || 'Wishlist action failed');
                  }
                }}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: isWishlisted(product.id) ? 'var(--orange)' : 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                {isWishlisted(product.id) ? '♥' : '♡'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                <span>📦</span> {t('products.shipping')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                <span>🛡</span> {t('products.warranty')}
              </div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section style={{ marginTop: '3rem' }}>
            <h2
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.35rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                marginBottom: '1.25rem',
              }}
            >
              {t('products.relatedProducts')}
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '1rem',
              }}
              className="related-grid"
            >
              {related.map((p) => (
                <Link key={p.id} to={`/products/${p.id}`} style={cardStyle}>
                  {p.photo_url ? (
                    <img
                      src={resolveMediaUrl(p.photo_url)}
                      alt={p.name}
                      style={{
                        width: '100%',
                        height: '140px',
                        objectFit: 'contain',
                        background: 'var(--bg-dark)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: '0.5rem',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '140px',
                        background: 'var(--bg-dark)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span style={{ fontSize: '1.5rem', opacity: 0.5 }}>🧴</span>
                    </div>
                  )}
                  <p
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </p>
                  <p style={{ color: 'var(--orange)', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem' }}>
                    {p.price} EGP
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      api.trackProductEvent({ productId: p.id, eventType: 'click', context: 'product_detail' }).catch(() => {});
                      addToCart(p);
                      showAlert(t('cart.addedToCart'));
                    }}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.35rem 0.5rem',
                      background: 'var(--orange)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}
                  >
                    {t('products.addToCart')}
                  </button>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section style={{ marginTop: '2.5rem' }}>
          <h2
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.25rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              marginBottom: '1rem',
            }}
          >
            {t('products.customerReviews')}
          </h2>
          <div
            style={{
              padding: '2rem',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <p style={{ marginBottom: '0.75rem' }}>{t('products.noReviews')}</p>
            <button
              type="button"
              style={{
                padding: '0.4rem 1rem',
                background: 'transparent',
                border: '1px solid var(--orange)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--orange)',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              {t('products.writeReview')}
            </button>
          </div>
        </section>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .product-detail-layout { grid-template-columns: 1fr !important; }
          .related-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 500px) {
          .related-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
